import {
  MdSettings,
  MdOutlineToggleOn,
  MdOutlineToggleOff,
  MdOutlineNavigateBefore,
  MdAdd,
  MdDelete,
  MdEdit,
} from "react-icons/md";
import { v4 as uuidv4 } from "uuid";

import PageTitle from "../../components/PageTitle/PageTitle";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import {
  API_ENDPOINT_UPDATE_PROFILE,
  BUTTON_STYLES,
  ICON_SIZES,
  PAYMENT_METHODS,
} from "../../helpers/constants";
import useMobile from "../../hooks/useMobile";

import InfoBlock from "../../components/InfoBlock/InfoBlock";
import { useMemo, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import apiClient from "../../helpers/api-client";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import { rgbaObjectToString, showNotification } from "../../helpers/utils";
import useCurrentUser from "../../hooks/useCurrentUser";
import CommonTable from "src/components/Tables/CommonTable";
import { ICellRendererParams } from "ag-grid-community";
import useGeneralOptionsDropdownMenu from "src/hooks/useGeneralOptionsDropdownMenu";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import { AnimatePresence } from "framer-motion";
import InputDialog from "src/components/InputDialog/InputDialog";
import useCollapsibles, {
  EPaymentMethodsMobileCollapsibles,
} from "src/hooks/useCollapsibles";
import moment from "moment";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";
import { EPaymentMethodsTableKeys } from "src/components/Tables/tableIds";

const PaymentMethodsPage: React.FC = () => {
  const { isMobile } = useMobile();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [isUpdatingMethods, setIsUpdatingMethods] = useState<boolean>(false);
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();
  const [showAddNewPaymentMethod, setShowAddNewPaymentMethod] =
    useState<boolean>(false);
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] =
    useState<boolean>(false);
  const {
    updateCollapsible,
    collapsiblePreferences,
    isPaymentMethodsMobileCollapsibleOpen,
    togglePaymentMethodsMobileCollapsible,
  } = useCollapsibles();
  const [customPaymentsSelectedRows, setCustomPaymentsSelectedRows] = useState<
    any[]
  >([]);

  const {
    enabledPaymentMethods,
    isLoadingAccounts,
    settings,
    allCustomPaymentMethods,
    getPaymentMethodById,
  } = useCurrentUser();

  const isEnabled = (methodId: string) => {
    return !!enabledPaymentMethods.includes?.(methodId);
  };

  const data = PAYMENT_METHODS().map((method: any) => ({
    name: method.name,
    id: method.id,
    enabled: isEnabled(method.id),
  }));

  const updatePaymentMethods = () => {
    setIsUpdatingMethods(true);

    const formData = new FormData();

    formData.append(
      "paymentMethods",
      JSON.stringify([...enabledPaymentMethods])
    );

    apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        showNotification({
          message: "Métodos de pago actualizados correctamente",
          kind: "success",
        });

        setShowConfirmDialog(false);
        // onUpdate();
      })
      .catch((e) => {
        showNotification({
          message: "Error actualizando tus métodos de pago",
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdatingMethods(false);
      });
  };

  const addCustomPaymentMethod = async (paymentMethodName: string) => {
    setIsAddingPaymentMethod(true);

    const formData = new FormData();

    formData.append(
      "customPaymentMethods",
      JSON.stringify([
        ...allCustomPaymentMethods,
        {
          name: paymentMethodName,
          id: uuidv4(),
          enabled: true,
          createdAt: new Date(),
          settings: {},
        },
      ])
    );

    await apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        showNotification({
          message: "Método de pago agregado correctamente",
          kind: "success",
        });

        setShowAddNewPaymentMethod(false);
        // onUpdate();
      })
      .catch((e) => {
        showNotification({
          message: "Error agregando el método de pago",
          kind: "error",
        });
      })
      .finally(() => {
        setIsAddingPaymentMethod(false);
      });
  };

  const columns: any[] = useMemo(() => {
    return [
      {
        field: "name",
        flex: 1,
        minWidth: 180,
        headerName: "NOMBRE",
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const methodData = getPaymentMethodById(params?.data?.id);
          const colors = {
            background: "#555",
            color: "#fff",
          };

          if (methodData?.settings) {
            colors.background = rgbaObjectToString(methodData?.settings?.bg);
            colors.color = rgbaObjectToString(methodData?.settings?.color);
          }

          return {
            params: {
              value: methodData?.name || params.value || "Desconocido",
            },
            custom: {
              ...colors,
            },
          };
        },
      },
      {
        headerName: "FONDO",
        cellRenderer: "CellColorRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: rgbaObjectToString(
                getPaymentMethodById(params?.data?.id)?.settings?.bg
              ),
            },
          };
        },
      },
      {
        headerName: "TEXTO",
        cellRenderer: "CellColorRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: rgbaObjectToString(
                getPaymentMethodById(params?.data?.id)?.settings?.color
              ),
            },
          };
        },
      },
      {
        cellRenderer: "CellWithButtonsRenderer",
        headerName: "ACCIONES",
        width: 80,
        cellRendererParams: (params: any) => {
          return {
            buttons: [
              {
                icon: params?.data?.enabled ? (
                  <MdOutlineToggleOn
                    size={ICON_SIZES.SM}
                    className="text-lime-400 dark:text-cyan-400 shrink-0"
                  />
                ) : (
                  <MdOutlineToggleOff
                    size={ICON_SIZES.SM}
                    className="text-red-400 dark:text-rose-400 shrink-0"
                  />
                ),
                onClick: () => {
                  // navigate(`/settings/categories/${params?.data?.category}`);
                },
                className: clsx(["grid-button"]),
              },
            ],
          };
        },
      },
    ];
  }, []);

  const customPaymentMethodsCols: any = useMemo(() => {
    return [
      {
        field: "name",
        flex: 1,
        minWidth: 180,
        headerName: "NOMBRE",
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const colors = {
            background: "#555",
            color: "#fff",
          };

          return {
            params: {
              ...params,
              value: params?.data?.name || "Sin nombre",
            },
            custom: colors,
          };
        },
      },
      {
        field: "createdAt",
        flex: 1,
        minWidth: 180,
        headerName: "FECHA DE CREACIÓN",
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const colors = {
            background: "#555",
            color: "#fff",
          };

          return {
            params: {
              ...params,
              value: moment(params?.data?.createdAt).format("yyyy-MM-DD"),
            },
            custom: colors,
          };
        },
      },
      {
        headerName: "FONDO",
        cellRenderer: "CellColorRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const colors = {
            background: "#555",
            color: "#fff",
          };

          return {
            params,
            custom: {
              ...colors,
            },
          };
        },
      },
      {
        headerName: "TEXTO",
        cellRenderer: "CellColorRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const colors = {
            color: "#555",
            background: "#fff",
          };
          return {
            params,
            custom: {
              ...colors,
            },
          };
        },
      },
      {
        cellRenderer: "CellWithButtonsRenderer",
        headerName: "ACCIONES",
        cellRendererParams: (params: any) => {
          return {
            buttons: [
              {
                icon: params?.data?.enabled ? (
                  <MdOutlineToggleOn
                    size={ICON_SIZES.SM}
                    className="text-lime-400 dark:text-cyan-400 shrink-0"
                  />
                ) : (
                  <MdOutlineToggleOff
                    size={ICON_SIZES.SM}
                    className="text-red-400 dark:text-rose-400 shrink-0"
                  />
                ),
                onClick: () => {
                  // navigate(`/settings/categories/${params?.data?.category}`);
                },
                className: clsx(["grid-button"]),
              },
              {
                icon: <MdEdit size={ICON_SIZES.SM} />,
                onClick: () => {
                  setShowEditDialog(true);
                },
                className: clsx(["grid-button"]),
                kind: EButtonKind.EDIT,
              },
              {
                icon: <MdDelete size={ICON_SIZES.SM} />,
                onClick: () => {
                  setShowDeleteDialog(true);
                },
                className: clsx(["grid-button"]),
                kind: EButtonKind.DANGER,
              },
            ],
          };
        },
      },
    ];
  }, []);

  const isLoadingSomething = useMemo(
    () => [isLoadingAccounts].includes(true),
    [isLoadingAccounts]
  );

  return (
    <>
      <TwoSidebars
        right={
          isLoadingSomething ? null : isMobile ? null : (
            <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
          )
        }
        isLoading={isLoadingSomething}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {isLoadingSomething ? (
              <LoadingWithTextPage />
            ) : (
              <>
                <PageTitle
                  titleLink="/settings"
                  icon={<MdSettings size={ICON_SIZES.MD} />}
                  title={t("settings_page.title")}
                  subtitle={t("settings.payment_methods_title")}
                  isLoading={false}
                  rightSide={
                    <>
                      <ButtonGroup
                        align="right"
                        gap={1}
                        buttons={[
                          {
                            icon: <MdAdd size={ICON_SIZES.SM} />,
                            tooltip: t("common_tooltips.add"),
                            onClick: () => setShowAddNewPaymentMethod(true),
                            className: isMobile ? BUTTON_STYLES.HEADING : "",
                          },
                          {
                            className: isMobile ? BUTTON_STYLES.HEADING : "",
                            icon: (
                              <MdOutlineNavigateBefore size={ICON_SIZES.MD} />
                            ),
                            tooltip: t("common_tooltips.back"),
                            onClick: () => {
                              navigate("/settings");
                            },
                          },
                          generalOptionsDropdown,
                        ]}
                      />
                    </>
                  }
                />

                <div className={clsx(["flex", "flex-col", "gap-4"])}>
                  <CollapsibleBlock
                    title="Métodos de pago integrados"
                    open={
                      isMobile
                        ? isPaymentMethodsMobileCollapsibleOpen(
                            EPaymentMethodsMobileCollapsibles.BUILTIN
                          )
                        : collapsiblePreferences.paymentMethodsBuiltInOpen
                    }
                    onChange={(isOpen: boolean) => {
                      if (isMobile) {
                        togglePaymentMethodsMobileCollapsible(
                          EPaymentMethodsMobileCollapsibles.BUILTIN
                        );
                      } else {
                        updateCollapsible({
                          paymentMethodsBuiltInOpen: isOpen,
                        });
                      }
                    }}
                  >
                    <div className={clsx(["flex", "flex-col", "gap-4"])}>
                      <InfoBlock
                        displayMobileIcon
                        message="No puedes editar ni eliminar los métodos de pago integrados, ya que son parte de eXpenser.net, pero sí puedes deshabilitar los que no necesites."
                      />
                      <CommonTable
                        singleSelection
                        onSelectionChange={() => {}}
                        data={data}
                        columns={columns}
                        settingsKey={
                          EPaymentMethodsTableKeys.BULTIN_PAYMENT_METHODS
                        }
                      />
                    </div>
                  </CollapsibleBlock>

                  <CollapsibleBlock
                    title="Métodos de pago personalizados"
                    open={
                      isMobile
                        ? isPaymentMethodsMobileCollapsibleOpen(
                            EPaymentMethodsMobileCollapsibles.CUSTOM
                          )
                        : collapsiblePreferences.paymentMethodsCustomOpen
                    }
                    onChange={(isOpen: boolean) => {
                      if (isMobile) {
                        togglePaymentMethodsMobileCollapsible(
                          EPaymentMethodsMobileCollapsibles.CUSTOM
                        );
                      } else {
                        updateCollapsible({
                          paymentMethodsCustomOpen: isOpen,
                        });
                      }
                    }}
                  >
                    <div className={clsx(["flex", "flex-col", "gap-4"])}>
                      <InfoBlock
                        displayMobileIcon
                        message={[
                          "Puedes agregar todos los métodos de pago personalizados que necesites.",
                          "Estos métodos de pago (al ser personalizados) puedes editarlos, habilitarlos y deshabilitarlos.",
                        ].join(" ")}
                      />
                      <CommonTable
                        onSelectionChange={(rows: any) =>
                          setCustomPaymentsSelectedRows(rows)
                        }
                        data={allCustomPaymentMethods}
                        columns={customPaymentMethodsCols}
                        singleSelection
                        settingsKey={
                          EPaymentMethodsTableKeys.CUSTOM_PAYMENT_METHODS
                        }
                      />
                      <InfoBlock
                        displayMobileIcon
                        message={[
                          "Para eliminar un método de pago personalizado asegúrate de que no se haya usado en alguna transacción.",
                        ].join(" ")}
                      />
                    </div>
                  </CollapsibleBlock>
                </div>
              </>
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>
      {showConfirmDialog && (
        <ConfirmDialog
          isLoading={isUpdatingMethods}
          onConfirm={async () => {
            updatePaymentMethods();
          }}
          onClose={() => setShowConfirmDialog(false)}
          description={t("dialogs.payment_methods.update_dialog_question")}
          title={t("dialogs.payment_methods.update_dialog_title")}
        />
      )}

      {showAddNewPaymentMethod && (
        <InputDialog
          onConfirm={async (value: any) => {
            await addCustomPaymentMethod(value);
          }}
          onClose={() => setShowAddNewPaymentMethod(false)}
          title="Agregar método de pago"
          description="Digita el nombre del método de pago que deseas agregar."
          confirmKind={EButtonKind.SAVE}
          type="text"
          placeholder="Método de pago"
          value=""
          isLoading={isAddingPaymentMethod}
        />
      )}

      {!!(showEditDialog && customPaymentsSelectedRows?.length === 1) && (
        <InputDialog
          onConfirm={async (value: any) => {
            // await addCustomPaymentMethod(value);
          }}
          onClose={() => setShowEditDialog(false)}
          title="Editar método de pago"
          description="Digita el nuevo nombre del método de pago."
          confirmKind={EButtonKind.SAVE}
          type="text"
          placeholder={customPaymentsSelectedRows[0]?.name}
          value={customPaymentsSelectedRows[0]?.name}
          isLoading={false}
        />
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          description="¿Deseas eliminar este método de pago?"
          title="Eliminar permanentemente"
          doubleConfirmRequired
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={() => {}}
          confirmKind={EButtonKind.DANGER}
          note="Esta acción no se puede deshacer."
        />
      )}
    </>
  );
};

export default PaymentMethodsPage;
